import { useMemo } from "react";
import { useI18n } from "Hooks";
import _ from "lodash";
import { useReduxState } from "SubjectStandardsV2/hooks";
import {
  useRepeatEvent,
  REPEAT_TYPES,
  END_TYPES,
} from "../../context/RepeatEventContext";
import { isStartDateAfterEndDate } from "Utils";

const getEventModalRepeatEventSettings = state => {
  return {
    repeatEventSettings: _.get(
      state,
      "myCalendar.eventModal.repeatEventSettings",
      {}
    ),
  };
};

export const useRepeatEventLogic = () => {
  const { t } = useI18n(["calendar", "common"]);
  const { state, actions } = useRepeatEvent();
  const { interval } = state;
  const { repeatEventSettings } = useReduxState(
    getEventModalRepeatEventSettings
  );

  // Options for repeat type dropdown
  const repeatTypeOptions = useMemo(
    () => [
      {
        value: REPEAT_TYPES.DAY,
        label: t(interval > 1 ? "calendar:day_unit" : "calendar:day"),
      },
      {
        value: REPEAT_TYPES.WEEK,
        label: t(interval > 1 ? "calendar:week_unit" : "calendar:week"),
      },
      {
        value: REPEAT_TYPES.MONTH,
        label: t(interval > 1 ? "calendar:month_unit" : "calendar:month"),
      },
    ],
    [interval]
  );

  // Get the unit label based on interval and repeat type
  const getUnitLabel = useMemo(() => {
    const { interval, repeatType } = state;

    if (interval === 1) {
      return t(`calendar:${repeatType}`);
    }

    return t(`calendar:${repeatType}_unit`);
  }, [state, t]);

  // Validation logic
  const isValidConfiguration = useMemo(() => {
    const {
      interval,
      endType,
      endDate,
      occurrences,
      academicYearEndDate,
      weekDays,
      repeatType,
    } = state;

    // Check if interval is valid
    if (interval < 1) return false;

    if (repeatType === REPEAT_TYPES.WEEK && _.isEmpty(weekDays)) return false;

    // Check end type specific validations
    switch (endType) {
      case END_TYPES.ON_DATE:
        if (!endDate) return false;
        // End date should not be before academic year end date
        if (
          isStartDateAfterEndDate({
            startDate: endDate,
            endDate: academicYearEndDate,
          })
        )
          return false;
        break;

      case END_TYPES.AFTER_OCCURRENCES:
        if (occurrences < 1) return false;
        break;

      default:
        break;
    }

    return true;
  }, [state]);

  // Handle interval change with validation
  const handleIntervalChange = value => {
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && numValue >= 1) {
      actions.setInterval(numValue);
    }
  };

  // Handle occurrences change with validation
  const handleOccurrencesChange = value => {
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && numValue >= 1) {
      actions.setOccurrences(numValue);
    }
  };

  // Handle end date change with validation
  const handleEndDateChange = date => {
    if (date && new Date(date) <= new Date(state.academicYearEndDate)) {
      actions.setEndDate(date);
    }
  };

  return {
    // State
    state,

    // Actions
    actions,

    // Computed values
    repeatTypeOptions,
    unitLabel: getUnitLabel,
    isValidConfiguration,
    // Enhanced handlers
    handleIntervalChange,
    handleOccurrencesChange,
    handleEndDateChange,
    repeatEventSettings,
    REPEAT_TYPES,
  };
};
